import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText, ShoppingCart, ClipboardList, BarChart2, Wrench,
  Database, Users, Lock, Zap, RefreshCw, Tag, AlertCircle,
  Truck, Building2, HardHat, Factory, Globe,
  Shield, BookOpen, Bell, ChevronDown, ChevronUp,
  AlertTriangle, Clock, Hash,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────
// DESIGN: Same V2 dark slate + amber gold — mobile optimized
// ─────────────────────────────────────────────────────────────

const FONT_URL = 'https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Mono:wght@400;500&family=Outfit:wght@300;400;500;600;700&display=swap';

const css = `
  :root {
    --gold: #C9972B;
    --gold-light: #F0C96A;
    --gold-dim: #7A5A18;
    --ink: #0F1117;
    --ink-mid: #1C2030;
    --ink-soft: #262D42;
    --text-primary: #F4F0E8;
    --text-secondary: #9EA8C0;
    --text-dim: #5C657A;
    --rule: #2A3048;
    --card-bg: #161C2E;
    --card-border: #222A40;
  }

  .ps-mobile-root {
    font-family: 'Outfit', sans-serif;
    background: #0F1117;
    color: #F4F0E8;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }

  .ps-mobile-root::before {
    content: '';
    position: fixed;
    inset: 0;
    background:
      radial-gradient(ellipse 80% 50% at 20% -10%, rgba(201,151,43,0.08) 0%, transparent 60%),
      radial-gradient(ellipse 60% 60% at 80% 110%, rgba(99,102,241,0.05) 0%, transparent 60%);
    pointer-events: none;
    z-index: 0;
  }

  .ps-mobile-inner {
    position: relative;
    z-index: 1;
    padding: 0 1rem 6rem;
  }

  .serif { font-family: 'DM Serif Display', Georgia, serif; }
  .mono  { font-family: 'DM Mono', monospace; }
`;

// ─────────────────────────────────────────────────────────────
// DATA (verbatim from V2 desktop)
// ─────────────────────────────────────────────────────────────

const stages = [
  {
    id: "design",
    num: "01",
    label: "Design Management",
    sub: "Every product starts with a spec.",
    Icon: FileText,
    iconBg: "background: rgba(14,165,233,0.15); color: #38bdf8;",
    accentColor: "#38bdf8",
    summary:
      "Engineering owns the technical content. Procurement tracks it, logs it, and ensures it translates to the project level. Design Management is the single source of truth for every product spec — SKUs, logistics details, storage requirements, nameplate photos, and the living record of every design change after approval. When a design changes, the downstream ripple — across commissioning checklists, spares, field mods — is visible before it becomes a problem.",
    bullets: [
      "Program documentation: upload, date-stamp, approval status per revision",
      "Design change tracking — field mods, safety redesigns, Ops redesigns, Delta Cube variants",
      "Logistics library: weight, dims, pallet count, truck count, packaging photos",
      "Storage requirements defined at the program level",
      "Quality system link: open SCARs and alerts surface on every vendor-product lookup",
      "Region-specific and AHJ requirements repository",
      "Tenant-specific and program-specific design requirement files",
    ],
  },
  {
    id: "buyout",
    num: "02",
    label: "Buyout",
    sub: "Turning plans into purchase orders.",
    Icon: ShoppingCart,
    iconBg: "background: rgba(16,185,129,0.15); color: #34d399;",
    accentColor: "#34d399",
    summary:
      "The OFE Plan from business leadership — megawatts, blocks, program, priority — becomes the input that drives everything downstream. Oracle POs capture the commercial reality. The gap today is that Oracle lines are entered manually, often with quantities greater than one, making unit-price analysis across all cost types nearly impossible. The future system explodes every multi-unit line automatically and ensures every line — freight, services, storage, change orders — carries a project allocation. Every single line. No exceptions.",
    caveat: "Today this is almost entirely manual — and the QTY > 1 problem is a real automation wall. Fixing this is foundational, not optional.",
    bullets: [
      "OFE Plan inputs: MW, blocks, design program, priority order drive system quantities",
      "Auto-populate Oracle PO data into Production Planners — no manual re-entry",
      "Smart line explosion: QTY > 1 on a PO line splits to one row per unit",
      "Unit prices enforced across all cost types: product, freight, services, misc",
      "Tax-applicability flag per project — never forgotten on a taxable PO",
      "Vendor Profiles: contacts, org chart, MSA tracking, milestone payments, quality assessments",
      "Auto-file every PO copy to a designated folder on creation",
    ],
  },
  {
    id: "production",
    num: "03",
    label: "Production Planning",
    sub: "One row. One asset. Full visibility.",
    Icon: ClipboardList,
    iconBg: "background: rgba(139,92,246,0.15); color: #a78bfa;",
    accentColor: "#a78bfa",
    summary:
      "This is where each product becomes its own asset. Every unit — quantity one per row — is tracked from manufacturing through first delivery. Vendors can access their own planner to update production status, ship dates, and serial numbers. Internal teams see on-time performance, storage addresses, and contractual dates. When a project gets reallocated, the system tracks the history. When a vendor updates a field, access controls ensure they only touch what they're permitted to touch.",
    bullets: [
      "QTY 1 per row — every unit is an individually tracked asset from day one",
      "Vendor-accessible: controlled by vendor, by planner, and by data column",
      "Production status, MFG ship date (plan vs actual), on-time performance",
      "Vendor storage tracking: address, out-of-storage date, first delivery destination",
      "Serial number assignment and capture at the unit level",
      "Contractual date vs requested delivery — gap always visible",
      "Access control by internal ADC team — not every team sees every planner",
    ],
  },
  {
    id: "project",
    num: "04",
    label: "Project Management",
    sub: "Schedule, dollars, and documents — unified.",
    Icon: BarChart2,
    iconBg: "background: rgba(245,158,11,0.15); color: #fbbf24;",
    accentColor: "#fbbf24",
    summary:
      "Project Management pulls from Production Planners and Oracle in real time. The Project Schedule auto-populates from vendor allocations. Project Financials roll up every PO line by project and PFR code, with a manual exposure layer for PM-entered risk costs. Submittals tracked internally with SharePoint links — not locked inside a GC-controlled Procore. Contacts drawn from the central Vendor Profile database. AI determines which costs need a Journal Entry to move between projects — and flags when the move is complete.",
    caveat: "Adding automation to submittal tracking is acknowledged as 'future-future' — the priority is ownership of the data trail first.",
    bullets: [
      "Project Schedule auto-filled from Production Planner allocations",
      "Reallocation history tracked — FinCode detail replaced by system audit trail",
      "Project Financials: PO + exposure subtotals, PFR code rollup, by-project view",
      "AI-assisted Journal Entry identification: which line, which project, which cost",
      "Submittals tracked internally — ADC-owned, not GC-gated",
      "Storage Locations database: type, address, POC, hours, insurance, warehouse ID",
      "Project Contacts: vendors from profiles, GC, EC, MC, EOR, CxA — all linked",
    ],
  },
  {
    id: "cx",
    num: "05",
    label: "CX & Field Scope",
    sub: "The last mile: from vendor to operations.",
    Icon: Wrench,
    iconBg: "background: rgba(244,63,94,0.15); color: #fb7185;",
    accentColor: "#fb7185",
    summary:
      "Procurement hands off to Construction — but the equipment's story doesn't end there. The future system tracks from GC storage through installation, commissioning stages, and turnover to Ops. CxAlloy becomes a data feed, not a silo. The Cx Look Ahead — a monthly report to all vendors showing project phase timelines — backfeeds into Production Planners so vendors always know where their equipment is heading. Serial number is the link across every handoff. The OFE Cx Playbook is a candidate for AI-assisted schedule sequencing.",
    caveat: "Procurement doesn't manage much additional data beyond what exists elsewhere in this plan. The biggest gap is anything that happens between Procurement delivery to GC storage and when CxAlloy picks it up.",
    bullets: [
      "Post-delivery status: GC storage \u2192 installation \u2192 Cx stages \u2192 Ops turnover",
      "CxAlloy integration: installation and beyond status feeds back into the system",
      "Cx Look Ahead: monthly vendor-facing schedule by project, phase, and L2\u2013RFS milestones",
      "Look Ahead data backfeeds Production Planners \u2014 vendors always see field timing",
      "3PL sync: product movement after delivery automated, no manual updates",
      "Serial number as the universal link from manufacture to operations",
      "OFE Cx Playbook: potential for AI-assisted sequencing and schedule automation",
    ],
  },
];

const systemPillars = [
  {
    Icon: Lock,
    iconStyle: "background: rgba(244,63,94,0.12); color: #fb7185;",
    title: "Access Control \u2014 Paramount",
    body: "User type governs everything. Vendors see only their planner, only permitted columns. Internal teams are scoped by function. External contractors get what they need and nothing more. This is the non-negotiable foundation.",
    tension: "The whole system breaks without this",
  },
  {
    Icon: Zap,
    iconStyle: "background: rgba(14,165,233,0.12); color: #38bdf8;",
    title: "Oracle Auto-Population",
    body: "Today, Procurement re-enters Oracle PO data by hand into every Production Planner. The future system reads Oracle directly \u2014 and when a line has QTY > 1, it explodes it into individual asset rows.",
    tension: "Currently manual \u2014 every time",
  },
  {
    Icon: Tag,
    iconStyle: "background: rgba(139,92,246,0.12); color: #a78bfa;",
    title: "Unit Prices Across All Costs",
    body: "Product enters Oracle at unit price. Freight, services, and storage often enter lump-sum as QTY 1. The system enforces unit-price capture universally \u2014 you cannot manage cost exposure you cannot see at the line level.",
    tension: "Core automation blocker today",
  },
  {
    Icon: RefreshCw,
    iconStyle: "background: rgba(16,185,129,0.12); color: #34d399;",
    title: "Live Reallocation Tracking",
    body: "When equipment moves from one project to another, every linked field \u2014 PO line, cost, schedule row \u2014 moves with it. History of every reallocation is preserved automatically. No more manual FinCode spreadsheets.",
    tension: "All manual today",
  },
  {
    Icon: Database,
    iconStyle: "background: rgba(245,158,11,0.12); color: #fbbf24;",
    title: "Serial Number as the Spine",
    body: "From the Production Planner to CxAlloy to the 3PL system, serial number is the universal key. Every handoff, every status change, every document traces back to one unique asset identifier.",
    tension: "The linking mechanism across everything",
  },
  {
    Icon: AlertCircle,
    iconStyle: "background: rgba(201,151,43,0.12); color: #C9972B;",
    title: "AI-Assisted Financials",
    body: "The system uses AI to identify which PO line costs need a Journal Entry to move between projects \u2014 and tracks when the entry is complete. Tax applicability flags prevent forgotten charges.",
    tension: "Highest leverage AI use case",
  },
];

const rememberItems = [
  { Icon: Hash,            title: "Tax applicability per project",      body: "No current Oracle flag. Without a system prompt, taxable POs get missed. Build the reminder in \u2014 don\u2019t rely on memory." },
  { Icon: Truck,           title: "All PO lines need allocation",       body: "Every freight, storage, and change order line needs a linked Project Allocation or it can\u2019t move when product gets reallocated." },
  { Icon: Clock,           title: "GC handoff gap",                     body: "Status tracking goes dark between Procurement delivery and CxAlloy pickup. Someone has to own that window \u2014 or the system has to flag it." },
  { Icon: AlertTriangle,   title: "Design change downstream effects",   body: "A redesign after approval hits Cx checklists, spares, and field mods. The system needs to surface those ripples automatically." },
  { Icon: Building2,       title: "Submittal access after GC turnover", body: "GC-controlled Procore goes dark after project close. ADC-owned copies in SharePoint are the only long-term record. Never skip the internal filing." },
  { Icon: Users,           title: "Vendor-specific column access",      body: "Vendor A must never see Vendor B\u2019s data \u2014 even in the same planner. Access must be enforced at the column and vendor level, not just the page level." },
];

const userTypes = [
  { label: "Procurement",           style: "color:#a78bfa; background:rgba(139,92,246,0.08); border-color:rgba(139,92,246,0.25);", internal: true },
  { label: "Engineering",           style: "color:#38bdf8; background:rgba(14,165,233,0.08); border-color:rgba(14,165,233,0.25);",  internal: true },
  { label: "Project Mgmt",          style: "color:#fbbf24; background:rgba(245,158,11,0.08);  border-color:rgba(245,158,11,0.25);",  internal: true },
  { label: "Vendors",               style: "color:#34d399; background:rgba(16,185,129,0.08);  border-color:rgba(16,185,129,0.25);",  internal: false },
  { label: "General Contractor",    style: "color:#fb923c; background:rgba(249,115,22,0.08);  border-color:rgba(249,115,22,0.25);",  internal: false },
  { label: "Integrators",           style: "color:#2dd4bf; background:rgba(20,184,166,0.08);  border-color:rgba(20,184,166,0.25);",  internal: false },
  { label: "3PL Warehouses",        style: "color:#60a5fa; background:rgba(59,130,246,0.08);  border-color:rgba(59,130,246,0.25);",  internal: false },
  { label: "CxA",                   style: "color:#f472b6; background:rgba(236,72,153,0.08);  border-color:rgba(236,72,153,0.25);",  internal: false },
  { label: "Electrical Contractor", style: "color:#818cf8; background:rgba(99,102,241,0.08);  border-color:rgba(99,102,241,0.25);",  internal: false },
  { label: "EOR",                   style: "color:#f87171; background:rgba(239,68,68,0.08);   border-color:rgba(239,68,68,0.25);",   internal: false },
  { label: "Operations",            style: "color:#94a3b8; background:rgba(148,163,184,0.08); border-color:rgba(148,163,184,0.25);", internal: false },
];

const connectedSystems = [
  { Icon: Database,  style: "color:#fb923c; background:rgba(249,115,22,0.1); border-color:rgba(249,115,22,0.2);",  label: "Oracle ERP",      blurb: "Auto-reads every PO line, explodes quantities, allocates every cost \u2014 no manual re-entry." },
  { Icon: Factory,   style: "color:#a78bfa; background:rgba(139,92,246,0.1); border-color:rgba(139,92,246,0.2);",  label: "CxAlloy",         blurb: "Commissioning and field status feeds back by serial number \u2014 from purchase order to operational asset." },
  { Icon: Truck,     style: "color:#34d399; background:rgba(16,185,129,0.1); border-color:rgba(16,185,129,0.2);",  label: "3PL Systems",     blurb: "Inventory movement synced automatically. The moment product leaves the warehouse, ADC knows \u2014 no phone call." },
  { Icon: Shield,    style: "color:#60a5fa; background:rgba(59,130,246,0.1); border-color:rgba(59,130,246,0.2);",  label: "ServiceNow",      blurb: "Eventually: quality events, SCARs, and service requests link to Vendor Profiles and Design Management." },
  { Icon: Globe,     style: "color:#38bdf8; background:rgba(14,165,233,0.1); border-color:rgba(14,165,233,0.2);",  label: "SharePoint",      blurb: "Submittals filed internally \u2014 ADC-owned, not locked in GC-controlled Procore after project close." },
  { Icon: BookOpen,  style: "color:#f472b6; background:rgba(236,72,153,0.1); border-color:rgba(236,72,153,0.2);",  label: "Procore / Helix", blurb: "GC-managed platforms tracked in parallel. ADC never loses visibility when a GC controls the primary tool." },
];

const lifecycleTags = [
  "Program Design","Vendor Qualification","PO Issuance",
  "Manufacturing","Logistics","First Delivery",
  "GC Storage","Installation","Commissioning","Ops Turnover",
];

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

function parseStyle(str) {
  const result = {};
  str.split(";").forEach(s => {
    const [k, v] = s.split(":").map(x => x.trim());
    if (k && v) {
      const camel = k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      result[camel] = v;
    }
  });
  return result;
}

function SectionLabel({ children }) {
  return (
    <p className="mono" style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--gold)", marginBottom: "0.75rem" }}>
      {children}
    </p>
  );
}

// ─────────────────────────────────────────────────────────────
// ACCORDION STAGE (mobile pattern from V1 mobile)
// ─────────────────────────────────────────────────────────────

function AccordionStage({ stage, expanded, onToggle }) {
  const { Icon } = stage;
  const isOpen = expanded === stage.id;

  return (
    <div style={{ marginBottom: 12 }}>
      <button
        onClick={() => onToggle(isOpen ? null : stage.id)}
        style={{
          width: "100%",
          background: isOpen ? "rgba(201,151,43,0.08)" : "var(--card-bg)",
          border: `1px solid ${isOpen ? "var(--gold)" : "var(--card-border)"}`,
          borderRadius: 16,
          padding: "1rem 1.1rem",
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          color: "var(--text-primary)",
          transition: "border-color 0.18s, background 0.18s",
        }}
      >
        <div style={{ ...parseStyle(stage.iconBg), width: 40, height: 40, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="mono" style={{ fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: isOpen ? "var(--gold-dim)" : "var(--text-dim)" }}>
            {stage.num}
          </div>
          <div style={{ fontSize: "0.95rem", fontWeight: 600, marginTop: 2 }}>
            {stage.label}
          </div>
          <div style={{ fontSize: "0.78rem", color: isOpen ? "var(--text-secondary)" : "var(--text-dim)", marginTop: 2 }}>
            {stage.sub}
          </div>
        </div>
        {isOpen
          ? <ChevronUp size={18} style={{ color: "var(--gold)", flexShrink: 0 }} />
          : <ChevronDown size={18} style={{ color: "var(--text-dim)", flexShrink: 0 }} />
        }
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: "hidden" }}
          >
            <div style={{ padding: "0.75rem 0.25rem 0" }}>
              <p style={{ fontSize: "0.85rem", lineHeight: 1.85, color: "var(--text-secondary)", marginBottom: "1rem" }}>
                {stage.summary}
              </p>
              {stage.caveat && (
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: "0.6rem",
                  background: "rgba(201,151,43,0.07)", border: "1px solid rgba(201,151,43,0.2)",
                  borderRadius: 10, padding: "0.75rem 1rem", marginBottom: "1rem",
                  fontSize: "0.8rem", lineHeight: 1.6, color: "var(--gold-light)",
                }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2, color: "var(--gold)" }} />
                  <span>{stage.caveat}</span>
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {stage.bullets.map((b) => (
                  <div key={b} style={{
                    display: "flex", alignItems: "flex-start", gap: "0.6rem",
                    border: "1px solid var(--card-border)", borderRadius: 10,
                    padding: "0.7rem 0.85rem", background: "rgba(255,255,255,0.02)",
                    fontSize: "0.8rem", lineHeight: 1.55, color: "var(--text-secondary)",
                  }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: stage.accentColor, flexShrink: 0, marginTop: 5 }} />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────

export default function ProcurementStoryV2Mobile() {
  const [expandedStage, setExpandedStage] = useState(null);
  const [rememberOpen, setRememberOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 600);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="ps-mobile-root">
      <link rel="stylesheet" href={FONT_URL} />
      <style>{css}</style>
      <div className="ps-mobile-inner">

        {/* ── HERO ─────────────────────────────────────────────── */}
        <section style={{ padding: "2.5rem 0 2rem" }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div style={{
              display: "inline-flex", alignItems: "center", gap: "0.5rem",
              border: "1px solid var(--gold-dim)", borderRadius: 100,
              padding: "0.35rem 1rem", fontSize: 11, fontWeight: 600,
              letterSpacing: "0.12em", textTransform: "uppercase",
              color: "var(--gold)", background: "rgba(201,151,43,0.07)",
              marginBottom: "1rem",
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--gold)" }} />
              ADC Procurement — Vision
            </div>
            <h1 className="serif" style={{
              fontSize: "clamp(2rem, 8vw, 3rem)", lineHeight: 1.1,
              letterSpacing: "-0.02em", margin: "0.5rem 0 1rem",
            }}>
              From design spec<br />
              to <em style={{ fontStyle: "italic", color: "var(--gold)" }}>operational</em><br />
              asset.
            </h1>
            <p style={{ fontSize: "0.95rem", lineHeight: 1.8, color: "var(--text-secondary)" }}>
              Today, almost every data point that moves between Design, Buyout,
              Production, and the Field is a manual entry. The future system
              connects all five domains — so data flows once, and every team
              always sees the truth.
            </p>
          </motion.div>

          {/* Core thesis card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            style={{ marginTop: "1.5rem" }}
          >
            <div style={{
              background: "var(--card-bg)", border: "1px solid var(--card-border)",
              borderRadius: 20, padding: "1.5rem", position: "relative", overflow: "hidden",
            }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg, var(--gold), transparent)" }} />
              <div className="mono" style={{ fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: "0.75rem" }}>
                Core thesis
              </div>
              <p style={{ fontSize: "0.85rem", lineHeight: 1.8, color: "var(--text-secondary)", marginBottom: "0.75rem" }}>
                User access control is paramount. Vendors see only their data.
                Teams see only what they need. Every unit of equipment is tracked
                as its own asset — from manufacture to operations — with serial
                number as the universal key.
              </p>
              <p style={{ fontSize: "0.85rem", lineHeight: 1.8, color: "var(--text-secondary)" }}>
                The same connected system that auto-populates Production Planners
                from Oracle also feeds the Project Schedule, rolls up financials
                by PFR code, and links quality events to every vendor-product lookup.
              </p>
            </div>
          </motion.div>
        </section>

        {/* ── 01 / FIVE DOMAINS (Accordion) ───────────────────── */}
        <section style={{ padding: "2rem 0", borderTop: "1px solid var(--rule)" }}>
          <div style={{ marginBottom: "1.5rem" }}>
            <SectionLabel>01 / The Five Domains</SectionLabel>
            <h2 className="serif" style={{ fontSize: "clamp(1.5rem, 5vw, 2rem)", lineHeight: 1.15, marginBottom: "0.5rem" }}>
              Start with design.<br />End with operations.
            </h2>
            <p style={{ fontSize: "0.875rem", lineHeight: 1.75, color: "var(--text-secondary)" }}>
              Five connected domains, one system. Each domain feeds the next.
              Data entered once propagates forward — no re-keying, no version drift, no lost handoffs.
            </p>
          </div>

          <div>
            {stages.map((stage) => (
              <AccordionStage
                key={stage.id}
                stage={stage}
                expanded={expandedStage}
                onToggle={setExpandedStage}
              />
            ))}
          </div>
        </section>

        {/* ── 02 / SYSTEM PILLARS ──────────────────────────────── */}
        <section style={{ padding: "2rem 0", borderTop: "1px solid var(--rule)" }}>
          <div style={{ marginBottom: "1.5rem" }}>
            <SectionLabel>02 / System Design Principles</SectionLabel>
            <h2 className="serif" style={{ fontSize: "clamp(1.5rem, 5vw, 2rem)", lineHeight: 1.15, marginBottom: "0.5rem" }}>
              Six rules the system<br />never breaks.
            </h2>
            <p style={{ fontSize: "0.875rem", lineHeight: 1.75, color: "var(--text-secondary)" }}>
              These aren't features — they're the structural commitments that make every other
              capability trustworthy. Get these wrong and the whole system collapses back into spreadsheets.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {systemPillars.map((p) => {
              const { Icon } = p;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  viewport={{ once: true }}
                  style={{
                    background: "var(--card-bg)", border: "1px solid var(--card-border)",
                    borderRadius: 16, padding: "1.25rem",
                  }}
                >
                  <div style={{ ...parseStyle(p.iconStyle), width: 40, height: 40, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "0.75rem" }}>
                    <Icon size={18} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: "0.4rem", color: "var(--text-primary)" }}>
                    {p.title}
                  </div>
                  <p style={{ fontSize: "0.82rem", lineHeight: 1.7, color: "var(--text-secondary)" }}>
                    {p.body}
                  </p>
                  {p.tension && (
                    <span className="mono" style={{
                      display: "inline-block", marginTop: "0.5rem",
                      fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase",
                      padding: "0.2rem 0.5rem", borderRadius: 4,
                      background: "rgba(201,151,43,0.1)", color: "var(--gold)",
                    }}>
                      {p.tension}
                    </span>
                  )}
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ── 03 / DID YOU REMEMBER ────────────────────────────── */}
        <section style={{ padding: "2rem 0", borderTop: "1px solid var(--rule)" }}>
          <div style={{ marginBottom: "1.5rem" }}>
            <SectionLabel>03 / Built-In Memory</SectionLabel>
            <h2 className="serif" style={{ fontSize: "clamp(1.5rem, 5vw, 2rem)", lineHeight: 1.15, marginBottom: "0.5rem" }}>
              "Did you remember?"
            </h2>
            <p style={{ fontSize: "0.875rem", lineHeight: 1.75, color: "var(--text-secondary)" }}>
              Some things don't happen consistently enough to be fully systematized — but they
              happen often enough to hurt when missed.
            </p>
          </div>

          <div style={{ background: "var(--card-bg)", border: "1px solid var(--gold-dim)", borderRadius: 20, overflow: "hidden" }}>
            <div
              onClick={() => setRememberOpen(!rememberOpen)}
              style={{
                display: "flex", alignItems: "center", gap: "0.75rem",
                padding: "1.25rem 1.25rem", borderBottom: rememberOpen ? "1px solid var(--rule)" : "none",
                cursor: "pointer", userSelect: "none",
              }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: 12,
                background: "rgba(201,151,43,0.15)", border: "1px solid var(--gold-dim)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--gold)", flexShrink: 0,
              }}>
                <Bell size={16} />
              </div>
              <div style={{ flex: 1 }}>
                <div className="serif" style={{ fontSize: "1.1rem", color: "var(--text-primary)" }}>
                  Pop-Up Reminder System
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: 2 }}>
                  Institutional knowledge encoded into the platform
                </div>
              </div>
              {rememberOpen
                ? <ChevronUp size={18} style={{ color: "var(--gold)", flexShrink: 0 }} />
                : <ChevronDown size={18} style={{ color: "var(--text-dim)", flexShrink: 0 }} />
              }
            </div>

            <AnimatePresence>
              {rememberOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                  style={{ overflow: "hidden" }}
                >
                  <div style={{ padding: "1.25rem" }}>
                    <p style={{ fontSize: "0.85rem", lineHeight: 1.8, color: "var(--text-secondary)", marginBottom: "1rem" }}>
                      The system should surface these at the right workflow moment,
                      not rely on the team to remember them under pressure.
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                      {rememberItems.map((item) => {
                        const { Icon } = item;
                        return (
                          <div key={item.title} style={{
                            display: "flex", alignItems: "flex-start", gap: "0.75rem",
                            background: "rgba(201,151,43,0.04)", border: "1px solid rgba(201,151,43,0.12)",
                            borderRadius: 12, padding: "0.85rem 1rem",
                          }}>
                            <div style={{
                              width: 28, height: 28, borderRadius: 8,
                              background: "rgba(201,151,43,0.1)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0, color: "var(--gold)",
                            }}>
                              <Icon size={13} />
                            </div>
                            <div>
                              <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--text-primary)", marginBottom: 3 }}>
                                {item.title}
                              </div>
                              <div style={{ fontSize: "0.78rem", lineHeight: 1.6, color: "var(--text-secondary)" }}>
                                {item.body}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        {/* ── 04 / USER TYPES ──────────────────────────────────── */}
        <section style={{ padding: "2rem 0", borderTop: "1px solid var(--rule)" }}>
          <div style={{ marginBottom: "1.5rem" }}>
            <SectionLabel>04 / Who Uses It</SectionLabel>
            <h2 className="serif" style={{ fontSize: "clamp(1.5rem, 5vw, 2rem)", lineHeight: 1.15, marginBottom: "0.5rem" }}>
              One system,<br />eleven stakeholders.
            </h2>
            <p style={{ fontSize: "0.875rem", lineHeight: 1.75, color: "var(--text-secondary)" }}>
              Access control is the foundation — not an afterthought. Every user type has a defined
              scope: what they can see, what they can edit, and which projects they can touch.
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {userTypes.map((u) => (
              <span key={u.label} style={{
                ...parseStyle(u.style),
                display: "inline-flex", alignItems: "center", gap: "0.4rem",
                borderRadius: 100, padding: "0.35rem 0.85rem",
                fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
                borderWidth: 1, borderStyle: "solid",
              }}>
                {u.internal && (
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", display: "inline-block" }} />
                )}
                {u.label}
                {u.internal && (
                  <span className="mono" style={{ fontSize: 8, letterSpacing: "0.1em", opacity: 0.6 }}>INTERNAL</span>
                )}
              </span>
            ))}
          </div>
          <div style={{ marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span style={{ fontSize: 11, color: "var(--text-dim)", display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--gold)", display: "inline-block" }} />
              Internal = ADC team with full or role-scoped access
            </span>
            <span style={{ fontSize: 11, color: "var(--text-dim)", marginLeft: 17 }}>
              All others: external access with column-level and project-level controls
            </span>
          </div>
        </section>

        {/* ── 05 / CONNECTED VISION ────────────────────────────── */}
        <section style={{ padding: "2rem 0", borderTop: "1px solid var(--rule)" }}>
          <div style={{ marginBottom: "1.5rem" }}>
            <SectionLabel>05 / The Connected System</SectionLabel>
            <h2 className="serif" style={{ fontSize: "clamp(1.5rem, 5vw, 2rem)", lineHeight: 1.15, marginBottom: "0.5rem" }}>
              One spine.<br />Every system reports to it.
            </h2>
            <p style={{ fontSize: "0.875rem", lineHeight: 1.75, color: "var(--text-secondary)" }}>
              The future procurement platform isn't a standalone tool — it's the connective tissue
              between Oracle, CxAlloy, ServiceNow, SharePoint, and every 3PL and GC system in the field.
            </p>
          </div>

          {/* End state card */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            viewport={{ once: true }}
            style={{ marginBottom: "1rem" }}
          >
            <div style={{
              background: "var(--card-bg)", border: "1px solid var(--card-border)",
              borderRadius: 20, padding: "1.5rem", position: "relative", overflow: "hidden",
            }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg, var(--gold), var(--gold-dim), transparent)" }} />
              <div style={{
                width: 44, height: 44, borderRadius: 14,
                background: "rgba(201,151,43,0.12)", border: "1px solid var(--gold-dim)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--gold)", marginBottom: "1rem",
              }}>
                <HardHat size={20} />
              </div>
              <div className="serif" style={{ fontSize: "1.25rem", lineHeight: 1.4, color: "var(--text-primary)", marginBottom: "0.75rem" }}>
                Data enters <em style={{ fontStyle: "italic", color: "var(--gold)" }}>once.</em><br />
                Every team sees the truth.
              </div>
              <p style={{ fontSize: "0.85rem", lineHeight: 1.8, color: "var(--text-secondary)", marginBottom: "0.75rem" }}>
                Today: a Procurement team member touches the same data point in Oracle,
                a Production Planner, a Project Schedule, and a financial report —
                four times, by hand, every time something changes.
              </p>
              <p style={{ fontSize: "0.85rem", lineHeight: 1.8, color: "var(--text-secondary)" }}>
                The future system: Oracle feeds the Planner. The Planner feeds the Schedule.
                The Schedule feeds the financial view. Serial numbers connect every system
                from manufacture to Ops. And AI handles the rest.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "1.25rem", paddingTop: "1rem", borderTop: "1px solid var(--rule)" }}>
                {lifecycleTags.map((tag) => (
                  <span key={tag} style={{
                    fontSize: 11, fontWeight: 500, borderRadius: 6,
                    padding: "0.25rem 0.6rem", background: "rgba(255,255,255,0.04)",
                    border: "1px solid var(--rule)", color: "var(--text-secondary)",
                  }}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Connected systems — single column */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {connectedSystems.map((item) => {
              const { Icon } = item;
              return (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  viewport={{ once: true }}
                  style={{
                    background: "var(--card-bg)", border: "1px solid var(--card-border)",
                    borderRadius: 14, padding: "1rem",
                    display: "flex", alignItems: "flex-start", gap: "0.85rem",
                  }}
                >
                  <div style={{
                    ...parseStyle(item.style),
                    width: 36, height: 36, borderRadius: 10,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, borderWidth: 1, borderStyle: "solid",
                  }}>
                    <Icon size={17} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--text-primary)", marginBottom: 3 }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: "0.78rem", lineHeight: 1.6, color: "var(--text-dim)" }}>
                      {item.blurb}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ── FOOTER ───────────────────────────────────────────── */}
        <div style={{
          marginTop: "2rem", paddingTop: "1.5rem",
          borderTop: "1px solid var(--rule)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: "0.75rem",
        }}>
          <span className="mono" style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--text-dim)" }}>
            ADC Procurement — Internal Vision
          </span>
          <span className="mono" style={{
            fontSize: 10, padding: "0.25rem 0.6rem",
            border: "1px solid var(--gold-dim)", borderRadius: 6,
            color: "var(--gold)", letterSpacing: "0.1em",
          }}>
            v2.0
          </span>
        </div>
      </div>

      {/* ── BACK TO TOP ──────────────────────────────────────── */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToTop}
            style={{
              position: "fixed", bottom: 24, right: 16, zIndex: 50,
              width: 44, height: 44, borderRadius: "50%",
              background: "var(--gold)", color: "var(--ink)",
              border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 20px rgba(201,151,43,0.4)",
            }}
            aria-label="Back to top"
          >
            <ChevronUp size={20} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
